import { Interface } from "@ethersproject/abi"
import invariant from "tiny-invariant"
import type { AbiItem } from "web3-utils"
import { getProvider } from "@wagmi/core"
import { formatEther, type BytesLike } from "ethers/lib/utils.js"

import type { TallyActionArgs } from "createProposal/action/types"
import type { ExecutableCallInput, ExecutableCall } from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import { getInputTypesAndValues } from "createProposal/helpers/action"
import type {
  AbiInputWithValue,
  AbiItemWithValues,
} from "createProposal/types/abi"
import TallyAction from "createProposal/action/TallyAction"
import { jsonStringify } from "common/helpers/serialization"
import { getAccountAddress } from "web3/helpers/accountId"
import { getChainIdParams } from "web3/helpers/chainId"
import { CHAIN_APIS } from "proposal/hooks/useBlockMetadata"
import {
  getAddresses,
  getContractData,
  getEndpoints,
} from "createProposal/hooks/useFetchAbi"
import { getAddressBookBridgeContract } from "createProposal/constants/address-book-bridge-contracts"

type CustomActionConstructorArgs = TallyActionArgs & {
  selectedAbiMethod?: AbiItemWithValues
}

class CustomAction extends TallyAction {
  hasUploadedFile: boolean
  isVerifiedOnBlockExplorer: boolean
  originalExecutableAction:
    | Omit<ExecutableCall, "offchaindata" | "index" | "chainId">
    | undefined

  abiMethods?: AbiItem[]
  selectedAbiMethod?: AbiItemWithValues

  constructor(args?: CustomActionConstructorArgs) {
    super(args)

    this.recipe = ExecutableCallType.Custom
    this.hasUploadedFile = false
    this.isVerifiedOnBlockExplorer = false
    this.originalExecutableAction = undefined

    if (args?.selectedAbiMethod) {
      this.selectedAbiMethod = args.selectedAbiMethod
    }
  }

  getTarget(): string {
    const { targetAddress } = this

    invariant(
      typeof targetAddress === "string",
      "Custom Recipe does not support multiple targets.",
    )

    return targetAddress
  }

  getCalldata(): string {
    const { selectedAbiMethod } = this

    invariant(selectedAbiMethod, "`selectedAbiMethod` must be defined")

    const { inputs } = selectedAbiMethod

    if (!inputs) {
      const calldata = super.getCalldata()

      invariant(
        typeof calldata === "string",
        "This custom action does not support multiple calldata.",
      )

      return calldata
    }

    const { values } = getInputTypesAndValues(inputs)

    const { name } = selectedAbiMethod

    invariant(name, "`name` must be defined")

    const abi = [selectedAbiMethod]

    const proposalInterface = new Interface(jsonStringify(abi))

    return proposalInterface.encodeFunctionData(name, values)
  }

  getSignature(): string {
    const { selectedAbiMethod } = this

    invariant(selectedAbiMethod, "abiMethod is undefined")

    const { name, inputs } = selectedAbiMethod
    const inputTypes = inputs?.map((input) => input.type)
    const signature = `${name}(${inputTypes ? inputTypes.join(",") : ""})`

    return signature
  }

  encodeForTallyApi(): ExecutableCallInput | ExecutableCallInput[] | undefined {
    const encode = super.encodeForTallyApi()

    if (!encode) {
      if (this.originalExecutableAction) {
        return this.originalExecutableAction as ExecutableCallInput
      }

      return undefined
    }

    if (this.abiMethods && this.selectedAbiMethod) {
      return {
        ...encode,
        offchaindata: JSON.stringify({
          abiMethods: this.abiMethods,
          selectedAbiMethod: this.selectedAbiMethod,
        }),
      }
    }

    return encode
  }

  setIsVerifiedOnBlockExplorer(isVerifiedOnBlockExplorer: boolean): this {
    this.isVerifiedOnBlockExplorer = isVerifiedOnBlockExplorer

    return this
  }

  setHasUploadedFile(hasUploadedFile: boolean): this {
    this.hasUploadedFile = hasUploadedFile

    return this
  }

  setAbiMethods(abiMethods: AbiItem[]): this {
    this.abiMethods = abiMethods

    return this
  }

  setOriginalExecutableAction(
    executableAction: Omit<
      ExecutableCall,
      "offchaindata" | "index" | "chainId"
    >,
  ): this {
    this.originalExecutableAction = executableAction

    return this
  }

  static async from(
    chainId: string,
    governorId: string,
    executableAction: ExecutableCallInput,
  ): Promise<CustomAction | undefined> {
    const targetAddress = getAccountAddress(executableAction.target)

    // Get ABI methods (from bridged contract or fetch from block explorer)
    const getAbiMethods = async () => {
      const bridgeContract = getAddressBookBridgeContract(
        governorId,
        targetAddress,
      )

      if (bridgeContract && bridgeContract.abiMethods) {
        return bridgeContract.abiMethods
      }

      const { reference: chainReference } = getChainIdParams(chainId)
      const provider = getProvider({ chainId: chainReference })
      const chain = CHAIN_APIS[chainReference]
      const addresses = await getAddresses(targetAddress, provider)
      const endpoints = getEndpoints(addresses, chain)
      const governorAbiItems = await getContractData(endpoints[0])
      const proxyAbiItems = endpoints[1]
        ? await getContractData(endpoints[1])
        : []
      const abiMethods = [...governorAbiItems, ...proxyAbiItems].flat()

      return abiMethods
    }

    //  Get ABI methods
    const abiMethods = await getAbiMethods()

    // If this CustomActoion is not verified on the block explorer, then set originalExecutableAction
    // data which won't be able to edit
    if (abiMethods.length === 0) {
      const nextAction = new CustomAction({
        targetAddress,
        value: formatEther(executableAction.value as string),
      })
        .setIsVerifiedOnBlockExplorer(false)
        .setOriginalExecutableAction({
          target: executableAction.target,
          signature: executableAction.signature,
          calldata: executableAction.calldata,
          value: executableAction.value,
          type: executableAction.type,
        })

      return nextAction
    }

    try {
      //  Decode ABI method
      const proposalInterface = new Interface(jsonStringify(abiMethods))
      const functionName = proposalInterface.getFunction(
        executableAction.signature as string,
      ).name
      const decoded = proposalInterface.decodeFunctionData(
        functionName,
        executableAction.calldata as BytesLike,
      )

      // Build selectedAbiMethod
      const selectedAbiMethod = abiMethods.find(
        (abiMethod) => abiMethod.name === functionName,
      )
      const inputs: AbiInputWithValue[] = selectedAbiMethod?.inputs
        ? selectedAbiMethod.inputs.map((abiInput, index) => ({
            ...abiInput,
            value: decoded[index],
          }))
        : []
      const selectedAbiMethodWithValues: AbiItemWithValues | undefined =
        selectedAbiMethod
          ? {
              ...selectedAbiMethod,
              inputs,
            }
          : undefined

      const nextAction = new CustomAction({
        targetAddress,
        value: formatEther(executableAction.value as string),
        selectedAbiMethod: selectedAbiMethodWithValues,
      })
        .setAbiMethods(abiMethods)
        .setIsVerifiedOnBlockExplorer(Boolean(abiMethods))

      return nextAction
    } catch {
      // If decoding action using verified ABI fails, then set originalExecutableAction
      // data which won't be able to edit
      const nextAction = new CustomAction({
        targetAddress,
        value: formatEther(executableAction.value as string),
      })
        .setIsVerifiedOnBlockExplorer(false)
        .setOriginalExecutableAction({
          target: executableAction.target,
          signature: executableAction.signature,
          calldata: executableAction.calldata,
          value: executableAction.value,
          type: executableAction.type,
        })

      return nextAction
    }
  }
}

export default CustomAction
